import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Appropriation } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner } from '../../ui/components';

export function Appropriations() {
  const { persona } = useAuth();
  const { fy, votes } = useRefData();
  const [voteFilter, setVoteFilter] = useState('');
  const [showEnter, setShowEnter] = useState(false);
  const [reviseTarget, setReviseTarget] = useState<Appropriation | null>(null);

  const query = useMemo(() => {
    if (!fy) return null;
    const p = new URLSearchParams({ fiscalYearId: fy.id, limit: '100' });
    if (voteFilter) p.set('voteId', voteFilter);
    return `/api/v1/warrants/appropriations?${p}`;
  }, [fy, voteFilter]);
  const listQ = useApiQuery<{ items: Appropriation[] }>(query);
  const combosQ = useApiQuery<{ items: Combination[] }>('/api/v1/coa/combinations?limit=100');
  const comboById = useMemo(() => new Map((combosQ.data?.items ?? []).map((c) => [c.id, c])), [combosQ.data]);

  const canEdit = persona?.roles.includes('MOF_TREASURY') ?? false;

  if (!fy) return <ErrorNote message="No OPEN fiscal year." />;

  return (
    <div>
      <PageHeader
        title="Appropriations"
        subtitle={`Enter each ministry's enacted budget allocation for FY${fy.year} — this is the ceiling all spending is checked against`}
        actions={
          canEdit ? (
            <button className="btn-primary" onClick={() => setShowEnter(true)}>
              + Enter appropriation
            </button>
          ) : undefined
        }
      />

      <div className="mb-4">
        <select className="field-input max-w-xs" value={voteFilter} onChange={(e) => setVoteFilter(e.target.value)}>
          <option value="">All votes</option>
          {votes.map((v) => (
            <option key={v.id} value={v.id}>
              {v.code} — {v.name}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading || combosQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No appropriations" hint="Enter the enacted budget per ministry budget line." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Budget line</th>
                <th className="table-head text-right">Original</th>
                <th className="table-head text-right">Revised</th>
                <th className="table-head">Entered</th>
                {canEdit ? <th className="table-head" /> : null}
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => {
                const combo = comboById.get(a.coaCombinationId);
                return (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="table-cell">
                      <div className="text-xs">{combo ? comboLabel(combo) : a.coaCombinationId}</div>
                    </td>
                    <td className="table-cell text-right tabular-nums">{scr(a.originalAmount)}</td>
                    <td className="table-cell text-right font-semibold tabular-nums">{scr(a.revisedAmount)}</td>
                    <td className="table-cell text-slate-500">{formatDate(a.createdAt)}</td>
                    {canEdit ? (
                      <td className="table-cell text-right">
                        <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setReviseTarget(a)}>
                          Revise
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showEnter ? (
        <EnterModal
          fyId={fy.id}
          onClose={() => setShowEnter(false)}
          onDone={() => {
            setShowEnter(false);
            listQ.reload();
          }}
        />
      ) : null}
      {reviseTarget ? (
        <ReviseModal
          appropriation={reviseTarget}
          label={comboById.get(reviseTarget.coaCombinationId) ? comboLabel(comboById.get(reviseTarget.coaCombinationId)!) : ''}
          onClose={() => setReviseTarget(null)}
          onDone={() => {
            setReviseTarget(null);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EnterModal({ fyId, onClose, onDone }: { fyId: string; onClose: () => void; onDone: () => void }) {
  const { votes } = useRefData();
  const [voteId, setVoteId] = useState('');
  const combosQ = useApiQuery<{ items: Combination[] }>(
    voteId ? `/api/v1/coa/combinations?voteId=${voteId}&active=true&limit=100` : null,
  );
  const [coaCombinationId, setCoaCombinationId] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/warrants/appropriations', {
        fiscalYearId: fyId,
        lines: [{ coaCombinationId, amount: Number.parseFloat(amount) }],
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to enter appropriation');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Enter appropriation (enacted budget)" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Ministry vote">
          <select
            className="field-input"
            value={voteId}
            onChange={(e) => {
              setVoteId(e.target.value);
              setCoaCombinationId('');
            }}
          >
            <option value="">Select vote…</option>
            {votes.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} — {v.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Budget line (CoA combination)">
          <select className="field-input" value={coaCombinationId} onChange={(e) => setCoaCombinationId(e.target.value)}>
            <option value="">Select budget line…</option>
            {(combosQ.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {comboLabel(c)}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Appropriated amount (SCR)">
          <input className="field-input" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !coaCombinationId || !amount}>
            {busy ? 'Saving…' : 'Enter appropriation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReviseModal({
  appropriation,
  label,
  onClose,
  onDone,
}: {
  appropriation: Appropriation;
  label: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [newAmount, setNewAmount] = useState(appropriation.revisedAmount);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/warrants/appropriations/${appropriation.id}/revise`, {
        newAmount: Number.parseFloat(newAmount),
        reason,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Revision failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Revise appropriation" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <p className="text-xs text-slate-500">{label}</p>
        <Field label={`New amount (current ${scr(appropriation.revisedAmount)})`}>
          <input className="field-input" type="number" min="0" step="0.01" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} />
        </Field>
        <Field label="Reason (supplementary budget, virement…)">
          <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !newAmount || reason.length < 3}>
            {busy ? 'Revising…' : 'Revise'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
